import React, { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import { sidebarLinks } from '../../utils/sidebarLinks'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import SearchField from '../../shared/SearchField'
import useCheckPermission from '../../hooks/useCheckPermission'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6'

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useSelector(state => state.auth)
  const { hasAnyModule } = useCheckPermission()
  const serviceId = user?.service?.id
  const { t } = useTranslation()
  const [filterSideBarLinks, setFilterSideBarLinks] = useState(sidebarLinks(t))
  const [filterText, setFilterText] = useState('')

  useEffect(() => {
    const search = filterText.trim().toLowerCase()
    const userRole = user?.role?.[0]?.name
    const isAdmin = userRole === 'Admin' || userRole === 'admin'

    const filtered = sidebarLinks(t).filter(item => {
      const matchesService = item?.serviceIds?.includes(serviceId)
      const hasModule = hasAnyModule(item?.modules)
      const matchesSearch = item?.label?.toLowerCase()?.includes(search)
      const isAdminOnlyLink = item?.adminOnly === true
      if (isAdminOnlyLink && !isAdmin) {
        return false
      }

      return matchesService && hasModule && matchesSearch
    })
    setFilterSideBarLinks(filtered)
  }, [filterText, t, hasAnyModule, serviceId, user])

  const handleFilter = e => setFilterText(e.target.value)
  return (
    <div
      className={`flex h-screen flex-col bg-primary transition-all duration-300 ${
        isCollapsed ? 'w-[90px]' : 'w-[180px] lg:w-[180px] xl:w-[210px]'
      }`}
    >
      {isCollapsed ? (
        <button
          type='button'
          onClick={() => navigate('/dashboard')}
          className='flex h-[56px] w-full shrink-0 items-center justify-center border-b border-white/20'
          title={t('Dashboard')}
        >
          <img
            src='/images/supergit-031.png'
            className='h-12 w-12 object-contain'
            alt='SuperGIT'
          />
        </button>
      ) : (
        <div className='flex items-center justify-center px-3 pt-[10px]'>
          <SearchField
            icon={filterText ? 'pi pi-times   cursor-pointer' : 'pi pi-search'}
            handleClearClick={() => setFilterText('')}
            value={filterText}
            onChange={handleFilter}
            className='!w-full'
            placeholder={t('Search')}
          />
        </div>
      )}
      <div className='overflow-y-auto flex-1'>
        <div className={`flex flex-col ${isCollapsed ? 'pt-1.5' : 'pt-4'}`}>
          {filterSideBarLinks?.map((data, index) => (
            <NavLink
              key={index}
              to={data?.link}
              title={data?.label}
              className={({ isActive }) =>
                `group sidebar-navlink ${
                  isCollapsed
                    ? `flex w-full flex-col items-center justify-center px-1 py-3 text-center ${
                        isActive || location.pathname === data?.link
                          ? 'bg-white/10'
                          : 'hover:bg-white/5'
                      }`
                    : `flex items-center gap-2 pe-1 ps-3 py-3 ${
                        isActive || location.pathname === data?.link
                          ? 'bg-[#1fb1ec]'
                          : ''
                      }`
                }`
              }
            >
              <div
                className={`flex items-center justify-center rounded-full border border-white transition-colors ${
                  isCollapsed
                    ? 'h-9 w-9 text-white/90 group-hover:border-white group-hover:text-white'
                    : 'h-[25px] w-[25px] text-white group-hover:bg-white group-hover:!text-primary'
                }`}
              >
                {data?.icon}
              </div>

              {isCollapsed ? (
                <p className='mt-1.5 px-1 text-center text-[11px] font-medium leading-tight text-white'>
                  {data?.label}
                </p>
              ) : (
                <p className='text-[14px] text-white font-[400] group-hover:text-white mt-1'>
                  {data?.label}
                </p>
              )}
            </NavLink>
          ))}
        </div>
      </div>
      <div className='border-t border-white/20 px-2 py-2'>
        <button
          type='button'
          onClick={onToggleCollapse}
          className={`flex w-full items-center text-white transition hover:bg-white/10 ${
            isCollapsed
              ? 'justify-center rounded-md py-2'
              : 'justify-center rounded-md py-2.5'
          }`}
          title={isCollapsed ? t('Expand Sidebar') : t('Collapse Sidebar')}
        >
          {isCollapsed ? (
            <FaChevronRight size={14} />
          ) : (
            <FaChevronLeft size={14} />
          )}
        </button>
      </div>
    </div>
  )
}

export default Sidebar
